import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Printer, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import useSharingTreeTagContent, { useTagArtwork } from "@/hooks/useSharingTreeTagContent";
import SharingTreeTagContentEditor from "@/components/SharingTreeTagContentEditor";
import useLabelPrintHistory, { labelPrintStatus, PRINT_STATUS_TEXT } from "@/hooks/useLabelPrintHistory";
import LabelPrintHistory from "@/components/LabelPrintHistory";
import {
  AVERY_CATALOG, SUPPORTED_CODES, normalizeProductCode, labelsPerSheet, slotRect,
  paginate, sheetCount, validateStart, formatLabel, labelIssues, labelContentLayout,
  splitToyLabels, validateToyItemsPerTag,
} from "@/lib/averyLabels";

const TITLES = { toys: "Toy labels", clothing: "Clothing labels", giftcards: "Gift card labels" };

const PRINT_CSS = `
.avery-print-blocked-msg{display:none}
@media print{
  @page{size:Letter;margin:0}
  html,body{margin:0!important;padding:0!important;background:#fff!important;height:auto!important;overflow:visible!important}
  body.avery-printing > *:not(.avery-print-root){display:none!important}
  .avery-print-root{position:static!important;display:block!important;background:none!important;overflow:visible!important;padding:0!important}
  .avery-print-root .avery-screen-only{display:none!important}
  .avery-print-root .avery-scroll{overflow:visible!important;max-height:none!important;padding:0!important;background:none!important}
  .avery-print-root .avery-sheets{display:block!important;gap:0!important;margin:0!important}
  .avery-print-root .avery-sheet{margin:0!important;box-shadow:none!important;break-after:page;page-break-after:always}
  .avery-print-root .avery-sheet:last-child{break-after:auto;page-break-after:auto}
  .avery-print-root .avery-cell{outline:0!important}
  .avery-print-root.avery-blocked .avery-sheets{display:none!important}
  .avery-print-root.avery-blocked .avery-print-blocked-msg{display:block!important;padding:1in;font:14pt sans-serif}
}`;

function Sheet({ product, cells, listType, opts, index, total, tag, artwork, onImageError, onImageLoad }) {
  const content = labelContentLayout(product, opts.orientation);
  const picture = tag.image_id && artwork.src ? (
    <img src={artwork.src} alt="" onError={onImageError} onLoad={onImageLoad}
         className="avery-artwork"
         style={{ display: "block", width: "100%", height: "0.4in", objectFit: "contain", margin: "0.025in 0" }} />
  ) : null;
  return (
    <div className="avery-sheet" data-testid={`avery-sheet-${index}`}
         style={{ position: "relative", width: "8.5in", height: "11in", background: "#fff", overflow: "hidden", flex: "none", boxSizing: "border-box" }}>
      {cells.map(({ slot, label }) => {
        const r = slotRect(product, slot);
        const f = formatLabel(label, listType, opts);
        return (
          <div key={label.key} className="avery-cell" data-label-key={label.key} data-slot={slot}
               style={{ position: "absolute", left: `${r.left}in`, top: `${r.top}in`, width: `${r.width}in`, height: `${r.height}in`,
                         boxSizing: "border-box", overflow: "hidden", outline: "1px dashed #bbb", outlineOffset: "-1px" }}>
            <div className="avery-content" data-label-key={label.key} data-orientation={opts.orientation}
                 style={{ position: "absolute", left: "50%", top: "50%", width: `${content.width}in`, height: `${content.height}in`,
                          transform: `translate(-50%, -50%) rotate(${content.rotation}deg)`, transformOrigin: "center",
                          boxSizing: "border-box", padding: "0.06in 0.1in", overflow: "hidden",
                          fontFamily: "Arial, Helvetica, sans-serif", fontSize: "9pt", lineHeight: 1.15, color: "#000", overflowWrap: "anywhere" }}>
              {tag.header && <div data-tag-header style={{ whiteSpace: "pre-wrap", fontWeight: 700, marginBottom: "0.04in" }}>{tag.header}</div>}
              {tag.image_position === "above" && picture}
              <div style={{ fontWeight: 700 }}>{f.head || "\u00A0"}</div>
              {f.body.map((t, i) => <div key={i}>{t}</div>)}
              <div style={{ fontWeight: 700, marginTop: "0.03in" }} data-ids>{f.ids}</div>
              {tag.image_position === "below" && picture}
              {tag.footer && <div data-tag-footer style={{ whiteSpace: "pre-wrap", marginTop: "0.04in" }}>{tag.footer}</div>}
            </div>
          </div>
        );
      })}
      <span className="avery-screen-only" style={{ position: "absolute", right: 4, bottom: 2, fontSize: 10, color: "#999" }}>Sheet {index + 1} of {total}</span>
    </div>
  );
}

export default function AveryLabelPrintModal({ year, listType, onClose }) {
  const tag = useSharingTreeTagContent(year);
  const history = useLabelPrintHistory(year, listType);
  const [printSnapshot, setPrintSnapshot] = useState(null);
  const frozen = !!printSnapshot && !!history.pending;
  const displayTag = frozen ? printSnapshot.tag : tag.settings;
  const artwork = useTagArtwork(year, displayTag.image_id);
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [artwork.src]);
  const [productInput, setProductInput] = useState("");
  const [genderLabels, setGender] = useState("boy_girl");
  const [sizeLabels, setSize] = useState("abbreviated");
  const [idLabels, setIds] = useState("short");
  const [sizes, setSizes] = useState({ pant: true, shirt: true, pj: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [start, setStart] = useState("1");
  const [orientation, setOrientation] = useState("horizontal");
  const [splitToys, setSplitToys] = useState(false);
  const [itemsPerTag, setItemsPerTag] = useState("2");
  const [overflow, setOverflow] = useState([]);
  const [imagesPending, setImagesPending] = useState(false);
  const [retry, setRetry] = useState(0);
  const [hideEmpty, setHideEmpty] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const reqId = useRef(0);
  const knownKeys = useRef(false);
  const previewRef = useRef(null);
  useEffect(() => { if (!history.pending) setPrintSnapshot(null); }, [history.pending]);
  useEffect(() => {
    const focus = () => { if (!history.pending && !history.busy) setRetry(n => n + 1); };
    window.addEventListener("focus", focus);
    return () => window.removeEventListener("focus", focus);
  }, [history.pending, history.busy]);
  const requestClose = useCallback(() => {
    if (tag.saving || tag.uploading || history.busy) return;
    if (history.pending && !window.confirm("This batch has not been confirmed as printed. Close without recording it?")) return;
    if (tag.dirty && !window.confirm("Close without saving this year's tag content?")) return;
    onClose();
  }, [onClose, tag.dirty, tag.saving, tag.uploading, history.pending, history.busy]);

  const { product, error: productError } = normalizeProductCode(productInput);

  // Fetch (stale requests cancelled). Selection survives refetches; keys are stable.
  useEffect(() => {
    const id = ++reqId.current;
    const ctrl = new AbortController();
    setLoading(true); setError("");
    api.get("/sharing-tree/labels", {
      params: { year, list_type: listType, gender_labels: genderLabels, size_labels: sizeLabels, id_labels: idLabels },
      signal: ctrl.signal,
    }).then(({ data: d }) => {
      if (id !== reqId.current) return;
      setData(d);
      const keys = new Set((d?.labels || []).map((l) => l.key));
      const initialize = !knownKeys.current;
      knownKeys.current = true;
      setSelected((prev) => {
         if (initialize) return keys;
        return new Set([...prev].filter((k) => keys.has(k)));
      });
      setLoading(false);
    }).catch((e) => {
      if (id !== reqId.current || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      setError(formatApiError(e)); setLoading(false);
    });
    return () => ctrl.abort();
  }, [year, listType, genderLabels, sizeLabels, idLabels, retry]);

  // Print scoping while open
  useEffect(() => {
    document.body.classList.add("avery-printing");
    return () => document.body.classList.remove("avery-printing");
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") requestClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [requestClose]);

  const labels = useMemo(() => data?.labels || [], [data]);
  const statuses = frozen ? printSnapshot.statuses : history.statuses;
  const visible = useMemo(() => labels.filter(label => {
    if (listType === "toys" && hideEmpty && (!Array.isArray(label.toy_items) ||
      !label.toy_items.every(item => typeof item === "string") || !label.toy_items.some(item => item.trim()))) return false;
    const status = labelPrintStatus(label, statuses);
    return statusFilter === "all" || (statusFilter === "printed" ? status === "printed" : status !== "printed");
  }), [labels, listType, hideEmpty, statusFilter, statuses]);
  const opts = useMemo(() => ({ sizes, orientation }), [sizes, orientation]);
  const yearMismatch = data && Number(data.year) !== Number(year);
  const issues = useMemo(() => labelIssues(visible, selected, listType, opts), [visible, selected, listType, opts]);
  const startError = product ? validateStart(start, product) : "";
  const splitting = listType === "toys" && splitToys;
  const splitError = splitting ? validateToyItemsPerTag(itemsPerTag) : "";
  const splitDataError = splitting && issues.chosen.some(label =>
    !Array.isArray(label.toy_items) || label.toy_items.some(item => typeof item !== "string"))
    ? "Structured toy items could not be loaded. Reload labels or turn off splitting." : "";
  const printable = useMemo(() => splitting
    ? (splitError ? [] : splitToyLabels(issues.chosen, itemsPerTag))
    : issues.chosen, [splitting, splitError, issues.chosen, itemsPerTag]);
  const emptyLabels = useMemo(() => printable.filter(label => formatLabel(label, listType, opts).empty), [printable, listType, opts]);
  const sheets = useMemo(() => (product && !startError ? paginate(printable, product, Number(start)) : []), [printable, product, start, startError]);
  const totalSheets = product && !startError ? sheetCount(printable.length, product, Number(start)) : 0;
  const displaySheets = frozen ? printSnapshot.sheets : sheets;

  const measure = useCallback(() => {
    const root = previewRef.current;
    if (!root) return;
    const bad = [];
    root.querySelectorAll(".avery-content").forEach((c) => {
      if (c.scrollHeight > c.clientHeight + 1 || c.scrollWidth > c.clientWidth + 1) bad.push(c.getAttribute("data-label-key"));
    });
    setImagesPending([...root.querySelectorAll(".avery-artwork")].some(img => !img.complete || !img.naturalWidth));
    setOverflow((prev) => (prev.join("|") === bad.join("|") ? prev : bad));
  }, []);
  useLayoutEffect(() => {
    measure();
    const root = previewRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [measure, displaySheets, opts, displayTag, artwork.src]);

  const blockers = [];
  if (!frozen && (tag.loading || tag.loadFailed)) blockers.push("Yearly tag settings have not loaded. Reload saved settings before printing.");
  if (tag.saving || tag.uploading) blockers.push("Tag content is still saving or uploading.");
  if (artwork.loading || imagesPending) blockers.push("Tag artwork is still loading.");
  if (artwork.error || imageFailed) blockers.push("Tag artwork could not be loaded. Retry the image or remove it before printing.");
  if (loading) blockers.push("Labels are still loading.");
  if (error) blockers.push("Labels could not be loaded.");
  if (!frozen && history.loading) blockers.push("Print history is still loading.");
  if (!frozen && history.error) blockers.push("Print history is unavailable. Reload history before printing.");
  if (history.busy) blockers.push("The print batch is being recorded or prepared.");
  if (history.canRecord && issues.chosen.some(label => !/^[a-f0-9]{64}$/.test(label.request_revision || ""))) {
    blockers.push("Request versions are unavailable. Reload labels before printing.");
  }
  if (yearMismatch) blockers.push(`Source year mismatch: data is for ${data.year}, not ${year}.`);
  if (productError) blockers.push(productError);
  if (startError) blockers.push(startError);
  if (splitError) blockers.push(splitError);
  if (splitDataError) blockers.push(splitDataError);
  if (!issues.chosen.length && data && !yearMismatch) blockers.push("Select at least one label.");
  if (emptyLabels.length) blockers.push(`${emptyLabels.length} selected label(s) would print with no ${listType === "toys" ? "gift text" : "size lines"}. Deselect them or enable more fields.`);
  if (overflow.length && !productError && !startError) blockers.push(`${overflow.length} label(s) overflow their cell. Choose a larger stock, fewer fields, or fewer tags.`);
  const canPrint = blockers.length === 0;
  const printSignature = useRef("");
  printSignature.current = JSON.stringify([year, listType, issues.chosen, productInput, start, opts,
    genderLabels, sizeLabels, idLabels, splitting, itemsPerTag, tag.settings, artwork.src]);
  async function beginPrint() {
    if (!canPrint || history.pending || history.busy) return;
    if (!history.canRecord) { window.print(); return; }
    const signature = printSignature.current;
    const snapshot = { tag: { ...tag.settings }, sheets, statuses: history.statuses };
    const prepared = await history.prepare({
      year: Number(year), list_type: listType,
      requests: issues.chosen.map(label => ({
        key: label.key, revision: label.request_revision,
        household_id: label.household_id, member_id: label.member_id,
      })),
      settings: {
        product: normalizeProductCode(productInput).code, start: Number(start), orientation,
        gender_labels: genderLabels, size_labels: sizeLabels, id_labels: idLabels, sizes,
        items_per_tag: splitting ? Number(itemsPerTag) : null, tag_content: { ...tag.settings },
      },
    }, printable.length, totalSheets);
    if (!prepared) return;
    setPrintSnapshot(snapshot);
    // Let the disabled controls/confirmation render before invoking native print.
    await new Promise(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
    if (!previewRef.current) return;
    const unsafe = [...previewRef.current.querySelectorAll(".avery-content")].some(c =>
      c.scrollHeight > c.clientHeight + 1 || c.scrollWidth > c.clientWidth + 1);
    const unfinished = [...previewRef.current.querySelectorAll(".avery-artwork")].some(img => !img.complete || !img.naturalWidth);
    if (signature !== printSignature.current || unsafe || unfinished) {
      history.discard("Not printed or recorded: the preview changed or is not ready. Reload labels and try again.");
      return;
    }
    try { window.print(); history.offered(prepared.id); }
    catch { history.discard("The print dialog could not be opened. Nothing was marked as printed."); }
  }
  async function confirmPrint() {
    if (await history.confirm()) setRetry(n => n + 1);
  }
  useEffect(() => {
    function beforePrint() {
      const root = previewRef.current;
      const unfinished = root && [...root.querySelectorAll(".avery-artwork")].some(img => !img.complete || !img.naturalWidth);
      const overflows = root && [...root.querySelectorAll(".avery-content")].some(c => c.scrollHeight > c.clientHeight + 1 || c.scrollWidth > c.clientWidth + 1);
      root?.closest(".avery-print-root")?.classList.toggle("avery-blocked", !canPrint || unfinished || overflows);
    }
    window.addEventListener("beforeprint", beforePrint);
    return () => window.removeEventListener("beforeprint", beforePrint);
  }, [canPrint]);

  function toggle(key) {
    setSelected((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }
  const border = { borderColor: "var(--fb-border)" };
  const chk = (label, value, set, testId) => (
    <label className="inline-flex items-center gap-1.5 text-sm">
      <input type="checkbox" checked={value} onChange={(e) => set(e.target.checked)} data-testid={testId} /> {label}
    </label>
  );

  return createPortal(
    <div className={`avery-print-root${canPrint ? "" : " avery-blocked"} fixed inset-0 z-[100] bg-white overflow-y-auto`} data-testid="avery-modal" role="dialog" aria-modal="true" aria-label={TITLES[listType]}>
      <style>{PRINT_CSS}</style>
      <div className="avery-print-blocked-msg" data-testid="avery-print-blocked-msg">
        Labels cannot be printed yet. Return to the label dialog and resolve the listed issues.
      </div>
      <div className="avery-screen-only p-4 sm:p-6 space-y-4 border-b" style={border}>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>{TITLES[listType]} — {year}</h3>
          <button type="button" onClick={requestClose} disabled={tag.saving || tag.uploading || !!history.busy} aria-label="Close label dialog" data-testid="avery-close"><X className="w-5 h-5" /></button>
        </div>

        <fieldset disabled={!!history.pending || !!history.busy} className="min-w-0 space-y-4">
        <legend className="sr-only">Label selection and print settings</legend>
        <div className="flex flex-wrap gap-4 items-end" data-testid="avery-filters">
          {listType === "toys" && chk("Hide requests with no toy items", hideEmpty, setHideEmpty, "avery-hide-empty")}
          <div>
            <label htmlFor="avery-status-filter" className="block text-sm font-semibold mb-1">Print status</label>
            <select id="avery-status-filter" data-testid="avery-status-filter" value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" style={border}>
              <option value="all">All requests</option><option value="needs">Needs printing</option><option value="printed">Printed</option>
            </select>
          </div>
          <button type="button" className="underline text-sm" data-testid="avery-reload-labels" onClick={() => setRetry(n => n + 1)}>Reload labels</button>
        </div>
        <p className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
          Needs printing means no confirmed print of the current request is recorded. Earlier printing may be unknown.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="avery-product" className="block text-sm font-semibold mb-1">Avery product</label>
            <input id="avery-product" list="avery-products" value={productInput} onChange={(e) => setProductInput(e.target.value)}
                   placeholder="Choose or type, e.g. Avery 5160" data-testid="avery-product"
                   className="w-full px-3 py-2 rounded-lg border" style={border} />
            <datalist id="avery-products">{SUPPORTED_CODES.map((c) => <option key={c} value={c} />)}</datalist>
            <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
              Supported: {Object.values(AVERY_CATALOG).map((p) => (
                <span key={p.code}><a href={p.sourceUrl} target="_blank" rel="noreferrer" className="underline">{[p.code, ...p.aliases].join("/")}</a>{" "}</span>
              ))}
              (Letter, no scaling; print at 100%).
            </p>
            {productError && productInput && <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }}>{productError}</p>}
          </div>
          <div>
            <label htmlFor="avery-start" className="block text-sm font-semibold mb-1">Start position{product ? ` (1–${labelsPerSheet(product)})` : ""}</label>
            <input id="avery-start" type="number" value={start} onChange={(e) => setStart(e.target.value)} data-testid="avery-start"
                   className="w-28 px-3 py-2 rounded-lg border tabular-nums" style={border} aria-invalid={!!startError} />
            <p className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>First sheet only, left to right then down.</p>
            {startError && <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }}>{startError}</p>}
          </div>
          <div className="text-sm" data-testid="avery-totals">
            <div className="font-semibold">Totals</div>
             {listType === "toys" && <div data-testid="avery-child-count">{issues.chosen.length} {issues.chosen.length === 1 ? "child" : "children"} selected</div>}
            <div>{printable.length} label(s) on {totalSheets} sheet(s)</div>
            {product && <div data-testid="avery-stock-details" className="mt-1 text-xs">
              Layout {product.code}: {product.width} × {product.height} inch labels,
              {" "}{labelsPerSheet(product)} per sheet ({product.cols} columns × {product.rows} rows).
              {" "}US Letter: 8.5 × 11 inches.
            </div>}
          </div>
        </div>

        <div className="max-w-md">
          <label htmlFor="avery-orientation" className="block text-sm font-semibold mb-1">Text orientation</label>
          <select id="avery-orientation" value={orientation} onChange={(e) => setOrientation(e.target.value)}
                  data-testid="avery-orientation" className="w-full px-3 py-2 rounded-lg border" style={border}
                  aria-describedby="avery-orientation-help">
            <option value="horizontal">Horizontal — long side left to right</option>
            <option value="vertical">Vertical — long side up and down</option>
          </select>
          <p id="avery-orientation-help" className="text-xs mt-1" style={{ color: "var(--fb-text-muted)" }}>
            {orientation === "vertical"
              ? "Text is rotated on the sheet so it reads upright when the label’s long side is vertical. The sheet stays in portrait orientation."
              : "Text reads across the label’s long side. The sheet stays in portrait orientation."}
          </p>
        </div>

        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {chk("Boy/Girl gender labels", genderLabels === "boy_girl", (v) => setGender(v ? "boy_girl" : "standard"), "avery-opt-gender")}
          {chk("Abbreviated sizes", sizeLabels === "abbreviated", (v) => setSize(v ? "abbreviated" : "standard"), "avery-opt-size")}
          {chk("Short IDs", idLabels === "short", (v) => setIds(v ? "short" : "standard"), "avery-opt-ids")}
          {listType === "clothing" && (<>
            {chk("Pant", sizes.pant, (v) => setSizes((s) => ({ ...s, pant: v })), "avery-line-pant")}
            {chk("Shirt", sizes.shirt, (v) => setSizes((s) => ({ ...s, shirt: v })), "avery-line-shirt")}
            {chk("PJ", sizes.pj, (v) => setSizes((s) => ({ ...s, pj: v })), "avery-line-pj")}
          </>)}
          {loading && data && <span className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Updating…</span>}
        </div>

        {listType === "toys" && (
          <fieldset className="max-w-md space-y-2 text-sm" data-testid="avery-toy-splitting">
            <legend className="font-semibold mb-1">Toy label splitting</legend>
            {chk("Split toy requests across tags", splitToys, setSplitToys, "avery-split-toys")}
            {splitToys && <div>
              <label htmlFor="avery-items-per-tag" className="block font-semibold mb-1">Toy items per tag</label>
              <input id="avery-items-per-tag" type="number" min="1" step="1" inputMode="numeric"
                     value={itemsPerTag} onChange={e => setItemsPerTag(e.target.value)}
                     data-testid="avery-items-per-tag" className="w-28 px-3 py-2 rounded-lg border"
                     style={border} aria-invalid={!!splitError} aria-describedby="avery-split-help" />
              {splitError && <p role="alert" className="mt-1" style={{ color: "var(--fb-error)" }}>{splitError}</p>}
            </div>}
            <p id="avery-split-help" className="text-xs" style={{ color: "var(--fb-text-muted)" }}>
              For example, 6 items at 2 per tag creates 3 labels. Saved requests are not changed.
            </p>
            {splitDataError && <button type="button" className="underline" data-testid="avery-retry-items"
              onClick={() => setRetry(n => n + 1)}>Reload toy items</button>}
          </fieldset>
        )}
        <SharingTreeTagContentEditor year={year} {...tag}
          onSave={tag.conflict ? undefined : tag.onSave}
          error={tag.conflict ? `${tag.error} Your draft is preserved. Reload saved settings to resolve this conflict.` : tag.error} />
        {(artwork.error || imageFailed) && (
          <button type="button" onClick={() => { setImageFailed(false); artwork.reload(); }}
                  className="text-sm underline" data-testid="tag-retry-image">Retry image</button>
        )}
        {error && (
          <div role="alert" className="p-3 rounded-xl fb-status-error text-sm flex items-center justify-between gap-3" data-testid="avery-error">
            <span>{error}</span>
            <button type="button" className="underline font-semibold" onClick={() => setRetry((n) => n + 1)} data-testid="avery-retry">Retry</button>
          </div>
        )}
        {loading && !data && !error && <div className="h-16 rounded-xl animate-pulse" style={{ background: "var(--fb-border)" }} data-testid="avery-loading" />}
        {data && labels.length === 0 && !error && (
          <div className="py-6 text-center text-sm" style={{ color: "var(--fb-text-muted)" }} data-testid="avery-empty">No labels available for {year}.</div>
        )}

        {labels.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-2 text-sm">
              <span className="font-semibold" data-testid="avery-visible-count">{listType === "toys" ? "Children" : "Labels"} ({issues.chosen.length}/{visible.length} visible selected; {labels.length - visible.length} hidden)</span>
              <button type="button" className="underline" data-testid="avery-select-all" onClick={() => setSelected(new Set(visible.map((l) => l.key)))}>Select all</button>
              <button type="button" className="underline" data-testid="avery-select-none" onClick={() => setSelected(new Set())}>Select none</button>
            </div>
            <ul className="max-h-48 overflow-y-auto border rounded-lg divide-y text-sm" style={border}>
              {visible.map((l) => (
                <li key={l.key} className="px-3 py-1.5 flex items-center gap-3" style={{ borderColor: "var(--fb-border)" }}>
                  <input type="checkbox" id={`avery-sel-${l.key}`} checked={selected.has(l.key)} onChange={() => toggle(l.key)} data-testid={`avery-sel-${l.key}`} />
                  <label htmlFor={`avery-sel-${l.key}`} className="flex-1 font-mono text-xs">{l.household_id} / {l.member_id} — {[l.gender, l.age].filter((x) => x !== null && x !== "").join(" ")}
                    <span className="block font-sans mt-1">{!frozen && (history.loading || history.error) ? "Print status unavailable" : PRINT_STATUS_TEXT[labelPrintStatus(l, statuses)]}</span>
                  </label>
                </li>
              ))}
            </ul>
            {!visible.length && <p className="text-sm py-3" data-testid="avery-filter-empty">No requests match these filters. Change the filters to see other requests.</p>}
          </div>
        )}

        {blockers.length > 0 && data && (
          <div role="alert" className="p-3 rounded-xl text-sm space-y-1" style={{ background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E" }} data-testid="avery-blockers">
            {blockers.map((b) => <div key={b} className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {b}</div>)}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <p className="text-xs mr-auto self-center w-full sm:w-auto sm:flex-1" style={{ color: "var(--fb-text-muted)" }}>
            Print on US Letter at actual size / 100%. Turn off browser headers and footers.
            Test on plain paper against your label sheet before printing on labels.
          </p>
          <button type="button" onClick={requestClose} disabled={tag.saving || tag.uploading} className="px-4 py-2.5 rounded-lg border text-sm" style={border}>Close</button>
          <button type="button" disabled={!canPrint || !!history.pending} onClick={beginPrint} data-testid="avery-print"
                  className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Printer className="w-4 h-4" /> Print {printable.length} label(s)
          </button>
        </div>
        </fieldset>
        <LabelPrintHistory history={{ ...history, confirm: confirmPrint }} />
      </div>

      <div className="avery-scroll overflow-x-auto p-4 sm:p-6" style={{ background: "#E7E5E0" }}>
        <div ref={previewRef} className="avery-sheets flex flex-col gap-4 w-max mx-auto">
          {product && !startError && displaySheets.map((cells, i) => (
            <Sheet key={i} product={product} cells={cells} listType={listType} opts={opts} index={i} total={displaySheets.length}
                   tag={displayTag} artwork={artwork} onImageError={() => setImageFailed(true)} onImageLoad={measure} />
          ))}
          {!product && <div className="avery-screen-only text-sm p-6" style={{ color: "var(--fb-text-muted)" }}>Choose an Avery product to see an exact-size preview.</div>}
        </div>
      </div>
    </div>,
    document.body
  );
}
